<template>
  <div data-testid="property-card" :data-property-id="property.id" :class="cardWrapperClass" @click="goToDetail">
    <div :class="imageWrapperClass">
      <img v-if="propertyImage" class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" :src="propertyImage" :alt="property.titulo">

      <div v-if="propertyTagLabel" class="absolute top-4 left-4 bg-base-100/80 backdrop-blur-md px-3 py-1 rounded-full text-[12px] font-bold text-primary uppercase tracking-wider shadow-sm">
        {{ propertyTagLabel }}
      </div>

      <IconCircleButton class="absolute top-4 right-4 hover:text-error" @click.stop="toggleFavorite"
        :title="isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'">
        <svg v-if="isFavorite" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-error" fill="currentColor" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-base-content" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      </IconCircleButton>

      <IconCircleButton class="absolute top-4 right-16 hover:text-primary"
        @click.stop="toggleComparison"
        :disabled="!canAddMore && !isPropertyInComparison"
        :title="comparisonTooltip">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      </IconCircleButton>
    </div>

    <div v-if="variant === 'compact'" class="space-y-2 px-1">
      <div class="text-primary font-bold text-xl">{{ formatPrice(property.precio) }}</div>
      <h3 class="text-base-content text-sm font-semibold leading-snug line-clamp-2">{{ property.titulo }}</h3>
      <div class="flex items-center text-base-content/70 gap-1">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 12.414A6 6 0 1112 13.414l4.243 4.243z" />
        </svg>
        <span class="text-xs font-medium truncate">{{ property.ubicacion }}</span>
      </div>
      <div class="mt-3 text-xs uppercase tracking-widest text-base-content/60 font-bold">{{ property.transaction_type }}</div>
    </div>

    <div v-else class="p-5 sm:p-6 lg:p-8 space-y-0">
      <div class="text-[24px] sm:text-[28px] leading-none font-extrabold text-base-content tracking-[-0.02em]">{{ formatPrice(property.precio) }}</div>
      <h3 class="mt-3 text-lg font-semibold leading-snug text-base-content line-clamp-2">
        {{ property.titulo }}
      </h3>

      <div class="flex items-center text-base-content/70 mt-3 mb-5 gap-1.5">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 12.414A6 6 0 1112 13.414l4.243 4.243z" />
        </svg>
        <span class="text-[15px] leading-[1.5] font-medium truncate">{{ property.ubicacion }}</span>
      </div>

      <div v-if="showFeatures" class="pt-3 pb-4 border-t border-base-200">
        <div class="flex items-center gap-4 sm:gap-6 overflow-x-auto pt-3">
          <div class="flex items-center gap-2 whitespace-nowrap text-base-content">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-base-content/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span class="text-sm font-semibold">{{ property.square_meters || property.totalArea || 0 }} m²</span>
          </div>
          <div class="flex items-center gap-2 whitespace-nowrap text-base-content">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-base-content/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span class="text-sm font-semibold">{{ property.habitaciones || 0 }} Hab.</span>
          </div>
          <div class="flex items-center gap-2 whitespace-nowrap text-base-content">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-base-content/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span class="text-sm font-semibold">{{ property.banos || 0 }} Baños</span>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between pt-3 gap-3">
        <button class="text-primary font-bold hover:underline flex items-center gap-2 text-[16px] sm:text-[18px] leading-tight min-h-[44px]">
          Ver detalles
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 12h15" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import IconCircleButton from '@/components/common/ui/IconCircleButton.vue'

export default {
  name: 'PropertyCard',
  components: {
    IconCircleButton
  },
  props: {
    property: {
      type: Object,
      required: true
    },
    variant: {
      type: String,
      default: 'default'
    },
    showTag: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isFavorite: this.property.is_favorite
    }
  },
  watch: {
    'property.is_favorite'(val) {
      this.isFavorite = val
    }
  },
  computed: {
    ...mapGetters('comparison', [
      'canAddMore',
      'isInComparison',
      'maxComparison'
    ]),
    cardWrapperClass() {
      return this.variant === 'compact'
        ? 'group bg-base-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 cursor-pointer p-5 sm:p-6 flex flex-col gap-4'
        : 'group bg-base-100 rounded-[24px] overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.03)] hover:translate-y-[-4px] transition-all duration-300 cursor-pointer border border-base-200'
    },
    imageWrapperClass() {
      return this.variant === 'compact'
        ? 'h-40 sm:h-32 rounded-xl overflow-hidden relative'
        : 'relative h-[240px] sm:h-[280px] overflow-hidden'
    },
    contentClass() {
      return this.variant === 'compact' ? '' : 'p-8 space-y-0'
    },
    priceClass() {
      return this.variant === 'compact'
        ? 'text-primary font-bold text-lg sm:text-xl'
        : 'text-[24px] sm:text-[28px] font-bold text-base-content'
    },
    showFeatures() {
      return this.variant === 'default'
    },
    propertyImage() {
      return this.property.imagenes && this.property.imagenes.length ? this.property.imagenes[0].imagen : ''
    },
    propertyTagLabel() {
      const value = (this.property.transaction_type || '').toString().toLowerCase()
      if (value.includes('alquil')) return 'En alquiler'
      if (value.includes('venta') || value.includes('sale')) return 'En venta'
      return 'En venta'
    },
    isPropertyInComparison() {
      return this.isInComparison(this.property.id)
    },
    comparisonTooltip() {
      if (this.isPropertyInComparison) return 'Quitar de comparación'
      if (this.canAddMore) return 'Agregar a comparación'
      return `Máximo ${this.maxComparison} propiedades para comparar`
    }
  },
  methods: {
    ...mapActions('comparison', [
      'addToComparison',
      'removeFromComparison'
    ]),
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return ''
      return `$${Number(value).toLocaleString('es-AR')}`
    },
    goToDetail() {
      this.$router.push(`/publications/${this.property.id}`)
    },
    async toggleFavorite() {
      if (!this.$store.getters['auth/isAuthenticated']) {
        this.$router.push({ name: 'login' })
        return
      }

      this.isFavorite = !this.isFavorite // Cambia el estado local inmediatamente
      try {
        await this.$store.dispatch('favorites/TOGGLE_FAVORITE', this.property.id)
      } catch (e) {
        this.isFavorite = !this.isFavorite
      }
    },
    async toggleComparison() {
      try {
        if (this.isPropertyInComparison) {
          await this.removeFromComparison(this.property.id)
          this.showToast('Propiedad removida de la comparación', 'info')
        } else if (this.canAddMore) {
          await this.addToComparison(this.property)
          this.showToast('Propiedad agregada a la comparación', 'success')
        }
      } catch (error) {
        this.showToast(error.message, 'error')
      }
    },
    showToast(message, type = 'info') {
      const event = new CustomEvent('show-toast', {
        detail: { message, type }
      })
      window.dispatchEvent(event)
    }
  }
}
</script>
